/**
 * SendReportDialog — R3/FE-4 (Monthly-Report-Paritaet)
 *
 * v1-Aequivalent: app/views/reports/email.haml + ReportsController#deliver_email.
 * Der v1-Dialog "Manually send email reports" bietet:
 *   - Monatsauswahl (available_report_months)
 *   - Zeitzonenauswahl (time_zone_select)
 *   - ein editierbares Feld fuer die EIGENE Account-Adresse (Full-Report)
 *   - je Kontakt ein editierbares E-Mail-Feld (kontaktgefilterter Report)
 *
 * Der bisherige v2-"Send test email"-Button verschickte stattdessen an die
 * Kontakte und kannte weder Monat, Zeitzone noch die eigene Adresse.
 *
 * Zielendpoint: POST /api/v2/monthly_reports/send_now (R3/BE-4, additiv).
 *
 * Der Dialog wird vom Aufrufer bedingt gerendert (unmount beim Schliessen).
 * Alle Formularwerte sind ABGELEITET aus den Server-Defaults und werden erst
 * durch eine Nutzereingabe ueberschrieben — daher kein State-Sync per Effect.
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Mail, X } from 'lucide-react'
import { useAvailableMonths, useTimeZones, useEmailRecipients } from './useReportMeta'
import { useSendMonthlyReports } from './useSendMonthlyReports'
import { monthLabel } from './reportUtils'

type Props = {
  onClose: () => void
  /** Vorbelegter Monat ("YYYY-MM") — i.d.R. die gerade angezeigte Periode. */
  initialMonth?: string
  /** Vorbelegte Zeitzone (Rails-Zonenname). */
  initialTimeZone?: string
}

type ContactOverride = { checked?: boolean; email?: string }

/**
 * Tageslimit des BE-Endpoints (POST /api/v2/monthly_reports/send_now):
 * maximal 20 E-Mails pro Tag bzw. pro Versand.
 */
export const DAILY_EMAIL_LIMIT = 20

export function SendReportDialog({ onClose, initialMonth, initialTimeZone }: Props) {
  const { t } = useTranslation('reports')
  const { months, defaultMonth } = useAvailableMonths()
  const { timeZones, defaultZone } = useTimeZones()
  const { recipients, isLoading: isLoadingRecipients } = useEmailRecipients(true)
  const { sendNow, isPending } = useSendMonthlyReports()

  // Nutzereingaben ueberschreiben die Server-Defaults (undefined = Default gilt).
  const [monthOverride, setMonthOverride] = useState<string | undefined>(undefined)
  const [timeZoneOverride, setTimeZoneOverride] = useState<string | undefined>(undefined)
  const [accountChecked, setAccountChecked] = useState(true)
  const [accountEmailOverride, setAccountEmailOverride] = useState<string | undefined>(undefined)
  const [contactOverrides, setContactOverrides] = useState<Record<number, ContactOverride>>({})
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const allowedMonths = useMemo(() => months.map((m) => m.value), [months])

  // R3-Review F6: BE-Labels sind immer englisch — clientseitig in der aktiven
  // Sprache formatieren, BE-Label nur als Fallback.
  const localizedMonths = useMemo(
    () => months.map((m) => ({ value: m.value, label: monthLabel(m.value, m.label) })),
    [months],
  )

  // Monat: bevorzugt die aktuell angezeigte Periode, sofern sie erlaubt ist.
  const month =
    monthOverride ??
    (initialMonth && allowedMonths.includes(initialMonth)
      ? initialMonth
      : (defaultMonth ?? allowedMonths[0] ?? ''))

  const timeZone = timeZoneOverride ?? initialTimeZone ?? defaultZone ?? ''

  // v1-Vorbelegung: account_email = current_user.email,
  // je Kontakt contact.email + Vorauswahl nach contact.monthly_report.
  const accountEmail = accountEmailOverride ?? recipients?.accountEmail ?? ''

  const contactRows = (recipients?.contacts ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    checked: contactOverrides[c.id]?.checked ?? c.monthlyReport,
    email: contactOverrides[c.id]?.email ?? c.email,
  }))

  const selectedCount =
    (accountChecked && accountEmail.trim() ? 1 : 0) +
    contactRows.filter((c) => c.checked && c.email.trim()).length

  // F-R3-4: Das BE deckelt den Versand bei DAILY_EMAIL_LIMIT Empfaengern pro Tag
  // (429 daily_limit_exceeded / 422 too_many_recipients). Der Dialog zeigt das
  // Limit jetzt vorab an, statt den Nutzer in den Fehler laufen zu lassen.
  const isOverLimit = selectedCount > DAILY_EMAIL_LIMIT

  function setContact(id: number, patch: ContactOverride) {
    setContactOverrides((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setResult(null)

    const contacts: Record<number, string> = {}
    contactRows.forEach((c) => {
      if (c.checked && c.email.trim()) contacts[c.id] = c.email.trim()
    })

    const outcome = await sendNow({
      month: month || undefined,
      timeZone: timeZone || undefined,
      recipients: {
        accountEmail: accountChecked && accountEmail.trim() ? accountEmail.trim() : null,
        contacts,
      },
    })

    if (outcome.ok) {
      setResult({ type: 'success', message: t('sendDialog.success', { queued: outcome.queued }) })
    } else {
      setResult({ type: 'error', message: outcome.error })
    }
  }

  return (
    <div className="send-report-dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        className="card card-pad send-report-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t('sendDialog.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="send-report-dialog-header">
          <h2>{t('sendDialog.title')}</h2>
          <button
            className="btn btn-sm btn-icon"
            type="button"
            onClick={onClose}
            aria-label={t('sendDialog.cancel')}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <p className="muted">{t('sendDialog.description')}</p>

        <form onSubmit={handleSubmit}>
          <div className="send-report-dialog-row">
            <label htmlFor="send-report-month">{t('sendDialog.month')}</label>
            <select
              id="send-report-month"
              className="report-filter-select"
              value={month}
              onChange={(e) => setMonthOverride(e.target.value)}
            >
              {localizedMonths.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          <div className="send-report-dialog-row">
            <label htmlFor="send-report-timezone">{t('sendDialog.timezone')}</label>
            <select
              id="send-report-timezone"
              className="report-filter-select"
              value={timeZone}
              onChange={(e) => setTimeZoneOverride(e.target.value)}
            >
              {!timeZones.some((tz) => tz.name === timeZone) && timeZone && (
                <option value={timeZone}>{timeZone}</option>
              )}
              {timeZones.map((tz) => (
                <option key={tz.name} value={tz.name}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          <fieldset className="send-report-dialog-recipients">
            <legend>{t('sendDialog.recipients')}</legend>

            {isLoadingRecipients && <p className="muted">{t('sendDialog.loadingRecipients')}</p>}

            <div className="send-report-recipient">
              <label className="send-report-recipient-check">
                <input
                  type="checkbox"
                  checked={accountChecked}
                  onChange={(e) => setAccountChecked(e.target.checked)}
                  aria-label={t('sendDialog.fullReport')}
                />
                <span>{t('sendDialog.fullReport')}</span>
              </label>
              <input
                type="email"
                className="input"
                value={accountEmail}
                onChange={(e) => setAccountEmailOverride(e.target.value)}
                disabled={!accountChecked}
                aria-label={t('sendDialog.accountEmail')}
                placeholder={t('sendDialog.accountEmail')}
              />
            </div>

            {contactRows.map((c) => (
              <div className="send-report-recipient" key={c.id}>
                <label className="send-report-recipient-check">
                  <input
                    type="checkbox"
                    checked={c.checked}
                    onChange={(e) => setContact(c.id, { checked: e.target.checked })}
                    aria-label={t('sendDialog.contactCheckbox', { name: c.name })}
                  />
                  <span>{c.name}</span>
                </label>
                <input
                  type="email"
                  className="input"
                  value={c.email}
                  onChange={(e) => setContact(c.id, { email: e.target.value })}
                  disabled={!c.checked}
                  aria-label={t('sendDialog.contactEmail', { name: c.name })}
                />
              </div>
            ))}

            {!isLoadingRecipients && contactRows.length === 0 && (
              <p className="muted">{t('sendDialog.noContacts')}</p>
            )}

            <p className="muted" data-testid="send-report-daily-limit">
              {t('sendDialog.dailyLimitHint', { limit: DAILY_EMAIL_LIMIT })}
            </p>

            {isOverLimit && (
              <p role="alert" className="text-danger" data-testid="send-report-limit-warning">
                {t('sendDialog.dailyLimitExceeded', {
                  limit: DAILY_EMAIL_LIMIT,
                  selected: selectedCount,
                })}
              </p>
            )}
          </fieldset>

          {result && (
            <p
              role="status"
              aria-live="polite"
              className={result.type === 'success' ? 'muted' : 'text-danger'}
            >
              {result.message}
            </p>
          )}

          <div className="send-report-dialog-actions">
            <button className="btn btn-sm" type="button" onClick={onClose}>
              {t('sendDialog.cancel')}
            </button>
            <button
              className="btn btn-sm btn-primary"
              type="submit"
              disabled={isPending || selectedCount === 0 || isOverLimit}
            >
              <Mail size={14} aria-hidden="true" />
              {isPending ? t('sendDialog.sending') : t('sendDialog.submit', { recipients: selectedCount })}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
